import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'

/**
 * Marva's reply text in the Ask dock, rendered as the Markdown the model writes
 * (MAR-213). Before this the bubble was `{m.content}` — newlines collapsed, so
 * `**bold**` and `- bullets` arrived as one run-on paragraph of raw syntax.
 *
 * ⚠️ **No raw HTML, ever.** The text is model output, and the model has read
 * shopper-authored content on the way to writing it. react-markdown escapes
 * HTML unless `rehype-raw` is added; do not add it. `allowedElements` is the
 * second wall: anything outside the list is unwrapped to its text, so a stray
 * `![img](…)` degrades to prose rather than to new UI.
 */

const HEADINGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const

const ALLOWED_ELEMENTS = [
  'p', 'ul', 'ol', 'li', 'strong', 'em', 'del', 'code', 'pre', 'a', 'br',
  'blockquote', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
  // GFM task lists and footnotes. `input` can only come from a `- [x]` item —
  // raw HTML is escaped — and remark-gfm always renders it `disabled`. Unwrapped,
  // it has no children, so done and not-done items would read the same.
  'input', 'sup', 'section',
  ...HEADINGS,
]

/**
 * A heading is shown as a bold paragraph. The bubble is 13.5px; a model-authored
 * `## Summary` rendered at heading size would shout over the reply it heads.
 */
const asBoldParagraph: Components['h1'] = ({ node, children }) => {
  void node
  return (
    <p>
      <strong>{children}</strong>
    </p>
  )
}

const components: Components = {
  ...Object.fromEntries(HEADINGS.map((h) => [h, asBoldParagraph])),
  // A link leaves the app rather than replacing it, and hands the new tab no
  // `window.opener` back into the merchant's session. react-markdown's default
  // `urlTransform` empties an unsafe href (`javascript:` and friends); an empty
  // href would open a copy of the app, so that link is shown as its text only.
  // A `#` link is a GFM footnote marker or back-reference: in a new tab it opens
  // a second copy of the app, and in place it can land in the wrong reply,
  // because every reply numbers its footnotes `user-content-fn-1` from one. The
  // notes sit directly under the text in a short bubble, so they are shown, not
  // linked.
  // `node` is the hast element react-markdown passes every override; it must
  // not be spread onto the DOM.
  a({ node, href, children, ...rest }) {
    void node
    if (!href || href.startsWith('#')) return <span>{children}</span>
    return (
      <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    )
  },
}

interface AssistantMarkdownProps {
  content: string
}

const AssistantMarkdown = ({ content }: AssistantMarkdownProps) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    allowedElements={ALLOWED_ELEMENTS}
    unwrapDisallowed
    components={components}
  >
    {content ?? ''}
  </ReactMarkdown>
)

export default AssistantMarkdown
